import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import { resumeAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  FileText, Save, Download, Sparkles, Loader2, User, Briefcase,
  GraduationCap, Award, FolderOpen, Plus, Trash2, Upload, Brain,
  ChevronDown, Camera, History, Wand2, BarChart3, CheckCircle,
  GripVertical, Pencil, X, RefreshCw, Layout,
  Columns2, Layers, Target, Zap, Send, Bot,
  Star, Eye, Bold, Italic, Underline, List, Link2,
  Palette, Share2, Copy, ExternalLink, Shield
} from 'lucide-react';

const ACCENT_COLORS = [
  '#0D9488', '#3B82F6', '#8B5CF6', '#EC4899', '#F5841F',
  '#DC2626', '#16A34A', '#6366F1', '#0EA5E9', '#D946EF',
];

const DEFAULT_SECTIONS = [
  { id: 'contact', label: 'Contact Information', icon: 'User', required: true, type: 'contact' },
  { id: 'summary', label: 'Professional Summary', icon: 'FileText', required: true, type: 'text' },
  { id: 'experience', label: 'Work Experience', icon: 'Briefcase', required: true, type: 'experience' },
  { id: 'education', label: 'Education', icon: 'GraduationCap', required: true, type: 'education' },
  { id: 'skills', label: 'Skills', icon: 'Award', required: true, type: 'skills' },
  { id: 'certifications', label: 'Certifications', icon: 'Award', type: 'list' },
  { id: 'projects', label: 'Projects', icon: 'FolderOpen', type: 'list' },
];

const ICON_MAP = { User, FileText, Briefcase, GraduationCap, Award, FolderOpen, Star, Target, Zap, Brain, Layers };

const TEMPLATES = [
  { id: 'classic', name: 'Classic', desc: 'Single column, ATS-optimized', cols: 1 },
  { id: 'modern', name: 'Modern', desc: 'Two-column with sidebar', cols: 2 },
  { id: 'impact', name: 'Impact', desc: 'Executive two-column (attached style)', cols: 2 },
  { id: 'executive', name: 'Executive', desc: 'Premium single-column', cols: 1 },
];

const SKILL_LEVELS = ['Beginner', 'Intermediate', 'Advanced', 'Expert'];
const TABS = [
  { id: 'editor', label: 'Editor', icon: Pencil },
  { id: 'preview', label: 'Preview', icon: Eye },
  { id: 'wizard', label: 'AI Wizard', icon: Wand2 },
  { id: 'versions', label: 'Versions', icon: History },
];

const emptyResume = {
  contact: { name: '', email: '', phone: '', location: '', linkedin: '', website: '' },
  summary: '', experience: [], education: [], skills: [], certifications: [], projects: [],
};

function calcCompleteness(r) {
  let f = 0;
  if (r.contact?.name && r.contact?.email) f++;
  if (r.summary?.length > 20) f++;
  if (r.experience?.length > 0) f++;
  if (r.education?.length > 0) f++;
  if (r.skills?.length > 0) f++;
  if (r.certifications?.length > 0) f++;
  if (r.projects?.length > 0) f++;
  return Math.round((f / 7) * 100);
}

// ---- Rich Text Toolbar ----
function RichTextArea({ value, onChange, placeholder, rows = 3 }) {
  const ref = useRef(null);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || '')) {
      ref.current.innerHTML = value || '';
    }
  }, []);

  const exec = (cmd, val) => { document.execCommand(cmd, false, val); ref.current?.focus(); };

  const handleInput = () => {
    if (ref.current) {
      let html = ref.current.innerHTML;
      html = html.replace(/\u2014/g, '-').replace(/\u2013/g, '-');
      onChange(html);
    }
  };

  return (
    <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${isFocused ? 'rgba(13,148,136,0.4)' : 'var(--t-border)'}` }}>
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 px-2 py-1" style={{ background: 'var(--t-bg-section)', borderBottom: '1px solid var(--t-border)' }}>
        {[
          { icon: Bold, cmd: 'bold', label: 'Bold' },
          { icon: Italic, cmd: 'italic', label: 'Italic' },
          { icon: Underline, cmd: 'underline', label: 'Underline' },
          { icon: List, cmd: 'insertUnorderedList', label: 'Bullet List' },
        ].map(t => (
          <button key={t.cmd} onClick={() => exec(t.cmd)}
            title={t.label}
            className="p-1.5 rounded hover:bg-white/10 transition-colors"
            style={{ color: 'var(--t-text-muted)' }}
            data-testid={`rt-${t.cmd}`}>
            <t.icon className="w-3.5 h-3.5" />
          </button>
        ))}
        <div className="w-px h-4 mx-1" style={{ background: 'var(--t-border)' }} />
        <button onClick={() => {
          const url = prompt('Enter URL:');
          if (url) exec('createLink', url);
        }} className="p-1.5 rounded hover:bg-white/10" style={{ color: 'var(--t-text-muted)' }}>
          <Link2 className="w-3.5 h-3.5" />
        </button>
      </div>
      {/* Editable area */}
      <div ref={ref} contentEditable suppressContentEditableWarning
        onInput={handleInput}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        className="px-3 py-2 text-sm outline-none"
        style={{
          minHeight: rows * 24,
          color: 'var(--t-text)',
          background: 'var(--t-bg-input)',
        }}
        data-placeholder={placeholder}
        data-testid="rich-text-area" />
    </div>
  );
}

// ---- Score Gauge ----
function ScoreGauge({ score, label, size = 44 }) {
  const color = score >= 80 ? '#16A34A' : score >= 60 ? '#F5841F' : '#DC2626';
  const r = (size - 4) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (score / 100) * circ;
  return (
    <div className="flex items-center gap-2">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--t-border)" strokeWidth={3} />
          <motion.circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={3}
            strokeLinecap="round" strokeDasharray={circ}
            initial={{ strokeDashoffset: circ }} animate={{ strokeDashoffset: offset }}
            transition={{ duration: 0.8 }} />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center text-[9px] font-black" style={{ color }}>{score}</span>
      </div>
      <span className="text-[9px] font-bold" style={{ color: 'var(--t-text-muted)' }}>{label}</span>
    </div>
  );
}

// ---- CONTACT EDITOR ----
function ContactEditor({ contact, update, photo, onPhotoUpload }) {
  const fileRef = useRef(null);
  const fields = [
    { key: 'name', label: 'Full Name', ph: 'John Doe' },
    { key: 'email', label: 'Email', ph: 'john@example.com' },
    { key: 'phone', label: 'Phone', ph: '+1 (555) 123-4567' },
    { key: 'location', label: 'Location', ph: 'San Francisco, CA' },
    { key: 'linkedin', label: 'LinkedIn', ph: 'linkedin.com/in/johndoe' },
    { key: 'website', label: 'Website', ph: 'johndoe.com' },
  ];
  return (
    <div className="flex items-start gap-4" data-testid="contact-editor">
      <div className="relative flex-shrink-0">
        <div className="w-20 h-20 rounded-2xl overflow-hidden flex items-center justify-center"
          style={{ background: 'var(--t-bg-section)', border: '2px dashed var(--t-border)' }}>
          {photo ? <img src={`data:image/jpeg;base64,${photo}`} alt="" className="w-full h-full object-cover" /> :
            <Camera className="w-6 h-6" style={{ color: 'var(--t-text-muted)' }} />}
        </div>
        <button onClick={() => fileRef.current?.click()}
          className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full flex items-center justify-center"
          style={{ background: '#0D9488', color: '#fff' }} data-testid="photo-upload-btn">
          <Camera className="w-3 h-3" />
        </button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPhotoUpload} />
      </div>
      <div className="flex-1 grid grid-cols-2 gap-3">
        {fields.map(f => (
          <div key={f.key}>
            <label className="text-[9px] font-bold uppercase tracking-wider block mb-1" style={{ color: 'var(--t-text-muted)' }}>{f.label}</label>
            <Input value={contact[f.key] || ''} onChange={e => update(`contact.${f.key}`, e.target.value)}
              placeholder={f.ph} className="h-9 text-sm"
              style={{ background: 'var(--t-bg-input)', borderColor: 'var(--t-border)', color: 'var(--t-text)' }}
              data-testid={`contact-${f.key}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ---- EXPERIENCE EDITOR ----
function ExperienceEditor({ items, update, onAdd, onRemove, onAiSuggest }) {
  return (
    <div className="space-y-3" data-testid="experience-editor">
      {items.map((exp, i) => (
        <div key={i} className="nx-card p-4" data-testid={`exp-${i}`}>
          <div className="flex items-start gap-2">
            <GripVertical className="w-4 h-4 mt-1 flex-shrink-0 cursor-grab" style={{ color: 'var(--t-text-muted)' }} />
            <div className="flex-1 space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <Input value={exp.title || ''} onChange={e => update(`experience.${i}.title`, e.target.value)}
                  placeholder="Job Title" className="h-8 text-sm"
                  style={{ background: 'var(--t-bg-input)', borderColor: 'var(--t-border)', color: 'var(--t-text)' }} />
                <Input value={exp.company || ''} onChange={e => update(`experience.${i}.company`, e.target.value)}
                  placeholder="Company" className="h-8 text-sm"
                  style={{ background: 'var(--t-bg-input)', borderColor: 'var(--t-border)', color: 'var(--t-text)' }} />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Input value={exp.location || ''} onChange={e => update(`experience.${i}.location`, e.target.value)}
                  placeholder="Location" className="h-8 text-sm"
                  style={{ background: 'var(--t-bg-input)', borderColor: 'var(--t-border)', color: 'var(--t-text)' }} />
                <Input value={exp.start_date || ''} onChange={e => update(`experience.${i}.start_date`, e.target.value)}
                  placeholder="Start Date" className="h-8 text-sm"
                  style={{ background: 'var(--t-bg-input)', borderColor: 'var(--t-border)', color: 'var(--t-text)' }} />
                <Input value={exp.end_date || ''} onChange={e => update(`experience.${i}.end_date`, e.target.value)}
                  placeholder="End Date" className="h-8 text-sm"
                  style={{ background: 'var(--t-bg-input)', borderColor: 'var(--t-border)', color: 'var(--t-text)' }} />
              </div>
              <RichTextArea
                value={(exp.bullets || []).join('<br/>')}
                onChange={v => update(`experience.${i}.bullets`, v.split(/<br\s*\/?>/).filter(Boolean))}
                placeholder="Describe your achievements and responsibilities..."
                rows={3} />
              <div className="flex gap-2">
                <button onClick={() => { const b = [...(exp.bullets || []), '']; update(`experience.${i}.bullets`, b); }}
                  className="text-[9px] font-bold flex items-center gap-1 px-2 py-1 rounded-lg"
                  style={{ color: '#0D9488', background: 'rgba(13,148,136,0.06)' }}>
                  <Plus className="w-3 h-3" /> Add Bullet
                </button>
                <button onClick={() => onAiSuggest('bullet', { job_title: exp.title, company: exp.company })}
                  className="text-[9px] font-bold flex items-center gap-1 px-2 py-1 rounded-lg"
                  style={{ color: '#8B5CF6', background: 'rgba(139,92,246,0.06)' }}>
                  <Sparkles className="w-3 h-3" /> AI Suggest
                </button>
              </div>
            </div>
            <button onClick={() => onRemove('experience', i)} className="p-1 hover:bg-red-500/10 rounded" data-testid={`remove-exp-${i}`}>
              <Trash2 className="w-3.5 h-3.5" style={{ color: '#DC2626' }} />
            </button>
          </div>
        </div>
      ))}
      <button onClick={() => onAdd('experience', { title: '', company: '', location: '', start_date: '', end_date: '', bullets: [''] })}
        className="w-full py-3 rounded-xl border-2 border-dashed flex items-center justify-center gap-2 text-xs font-bold"
        style={{ borderColor: 'var(--t-border)', color: 'var(--t-text-muted)' }} data-testid="add-experience-btn">
        <Plus className="w-4 h-4" /> Add Experience
      </button>
    </div>
  );
}

// ---- EDUCATION EDITOR ----
function EducationEditor({ items, update, onAdd, onRemove }) {
  return (
    <div className="space-y-3" data-testid="education-editor">
      {items.map((edu, i) => (
        <div key={i} className="nx-card p-3 flex items-center gap-3" data-testid={`edu-${i}`}>
          <GraduationCap className="w-5 h-5 flex-shrink-0" style={{ color: '#3B82F6' }} />
          <div className="flex-1 grid grid-cols-3 gap-2">
            <Input value={edu.degree || ''} onChange={e => update(`education.${i}.degree`, e.target.value)} placeholder="Degree"
              className="h-8 text-sm" style={{ background: 'var(--t-bg-input)', borderColor: 'var(--t-border)', color: 'var(--t-text)' }} />
            <Input value={edu.school || ''} onChange={e => update(`education.${i}.school`, e.target.value)} placeholder="School"
              className="h-8 text-sm" style={{ background: 'var(--t-bg-input)', borderColor: 'var(--t-border)', color: 'var(--t-text)' }} />
            <Input value={edu.year || ''} onChange={e => update(`education.${i}.year`, e.target.value)} placeholder="Year"
              className="h-8 text-sm" style={{ background: 'var(--t-bg-input)', borderColor: 'var(--t-border)', color: 'var(--t-text)' }} />
          </div>
          <button onClick={() => onRemove('education', i)} className="p-1 hover:bg-red-500/10 rounded">
            <Trash2 className="w-3 h-3" style={{ color: '#DC2626' }} />
          </button>
        </div>
      ))}
      <button onClick={() => onAdd('education', { degree: '', school: '', year: '' })}
        className="w-full py-2.5 rounded-xl border-2 border-dashed flex items-center justify-center gap-2 text-xs font-bold"
        style={{ borderColor: 'var(--t-border)', color: 'var(--t-text-muted)' }} data-testid="add-edu-btn">
        <Plus className="w-3.5 h-3.5" /> Add Education
      </button>
    </div>
  );
}

// ---- SKILLS EDITOR ----
function SkillsEditor({ items, update, onAiSuggest }) {
  return (
    <div className="space-y-3" data-testid="skills-editor">
      <div className="flex flex-wrap gap-2">
        {items.map((s, i) => (
          <div key={i} className="flex items-center gap-1 px-2.5 py-1.5 rounded-xl group"
            style={{ background: 'var(--t-bg-section)', border: '1px solid var(--t-border)' }} data-testid={`skill-${i}`}>
            <Input value={s.name || ''} onChange={e => update(`skills.${i}.name`, e.target.value)}
              placeholder="Skill" className="h-6 w-24 text-xs border-none bg-transparent p-0"
              style={{ color: 'var(--t-text)' }} />
            <select value={s.level || 'Intermediate'} onChange={e => update(`skills.${i}.level`, e.target.value)}
              className="text-[8px] font-bold bg-transparent border-none outline-none cursor-pointer"
              style={{ color: '#0D9488' }}>
              {SKILL_LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
            <button onClick={() => update('skills', items.filter((_, j) => j !== i))}
              className="opacity-0 group-hover:opacity-100"><X className="w-3 h-3" style={{ color: '#DC2626' }} /></button>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <button onClick={() => update('skills', [...items, { name: '', level: 'Intermediate' }])}
          className="text-[10px] font-bold flex items-center gap-1 px-3 py-1.5 rounded-lg"
          style={{ color: '#0D9488', background: 'rgba(13,148,136,0.06)' }} data-testid="add-skill-btn">
          <Plus className="w-3 h-3" /> Add Skill
        </button>
        <button onClick={() => onAiSuggest('skills', {})}
          className="text-[10px] font-bold flex items-center gap-1 px-3 py-1.5 rounded-lg"
          style={{ color: '#8B5CF6', background: 'rgba(139,92,246,0.06)' }}>
          <Sparkles className="w-3 h-3" /> AI Suggest
        </button>
      </div>
    </div>
  );
}

// ---- LIST EDITOR (certs / projects / custom) ----
function ListEditor({ items, section, fields, update, onAdd, onRemove }) {
  return (
    <div className="space-y-2" data-testid={`${section}-editor`}>
      {items.map((item, i) => (
        <div key={i} className="nx-card p-3 flex items-start gap-2" data-testid={`${section}-${i}`}>
          <div className="flex-1 grid grid-cols-2 gap-2">
            {fields.map(f => f.rich ? (
              <div key={f.key} className="col-span-2">
                <RichTextArea value={item[f.key] || ''} onChange={v => update(`${section}.${i}.${f.key}`, v)}
                  placeholder={f.ph} rows={2} />
              </div>
            ) : (
              <Input key={f.key} value={item[f.key] || ''} onChange={e => update(`${section}.${i}.${f.key}`, e.target.value)}
                placeholder={f.ph} className="h-8 text-sm"
                style={{ background: 'var(--t-bg-input)', borderColor: 'var(--t-border)', color: 'var(--t-text)' }} />
            ))}
          </div>
          <button onClick={() => onRemove(section, i)} className="p-1 hover:bg-red-500/10 rounded">
            <Trash2 className="w-3 h-3" style={{ color: '#DC2626' }} />
          </button>
        </div>
      ))}
      <button onClick={() => onAdd(section, fields.reduce((a, f) => ({ ...a, [f.key]: '' }), {}))}
        className="w-full py-2 rounded-xl border-2 border-dashed flex items-center justify-center gap-2 text-xs font-bold"
        style={{ borderColor: 'var(--t-border)', color: 'var(--t-text-muted)' }} data-testid={`add-${section}-btn`}>
        <Plus className="w-3.5 h-3.5" /> Add Item
      </button>
    </div>
  );
}


// ---- IMPACT TEMPLATE PREVIEW (from attached DOCX style) ----
function ImpactPreview({ resume, photo, accentColor }) {
  const c = resume.contact || {};
  const ac = accentColor || '#0D9488';
  return (
    <div className="bg-white text-gray-900 rounded-xl overflow-hidden shadow-lg" style={{ fontSize: 11 }} data-testid="resume-preview">
      {/* Top Banner */}
      <div className="px-6 py-4" style={{ background: ac, color: '#fff' }}>
        <div className="flex items-center gap-4">
          {photo && <img src={`data:image/jpeg;base64,${photo}`} alt="" className="w-14 h-14 rounded-full object-cover border-2 border-white/30" />}
          <div>
            <h1 className="text-xl font-black uppercase tracking-wide">{c.name || 'YOUR NAME'}</h1>
            <p className="text-xs opacity-90">{resume.experience?.[0]?.title || 'Professional Title'}</p>
          </div>
        </div>
      </div>
      {/* Impact at a Glance */}
      {resume.summary && (
        <div className="px-6 py-3" style={{ background: `${ac}08`, borderBottom: `2px solid ${ac}` }}>
          <p className="text-[8px] font-black uppercase tracking-wider mb-1" style={{ color: ac }}>Impact at a Glance</p>
          <p className="text-[10px] leading-relaxed text-gray-700" dangerouslySetInnerHTML={{ __html: resume.summary }} />
        </div>
      )}
      <div className="flex">
        {/* Left Sidebar */}
        <div className="w-[32%] p-4 space-y-4" style={{ background: '#f8f9fa', borderRight: '1px solid #e5e7eb' }}>
          {/* Contact */}
          <div>
            <p className="text-[7px] font-black uppercase tracking-widest mb-1.5" style={{ color: ac }}>Contact</p>
            {c.email && <p className="text-[8px] text-gray-600">{c.email}</p>}
            {c.phone && <p className="text-[8px] text-gray-600">{c.phone}</p>}
            {c.location && <p className="text-[8px] text-gray-600">{c.location}</p>}
            {c.linkedin && <p className="text-[8px]" style={{ color: ac }}>{c.linkedin}</p>}
          </div>
          {/* Skills */}
          {resume.skills?.length > 0 && (
            <div>
              <p className="text-[7px] font-black uppercase tracking-widest mb-1.5" style={{ color: ac }}>Technical Arsenal</p>
              {resume.skills.map((s, i) => (
                <div key={i} className="flex items-center justify-between mb-0.5">
                  <span className="text-[8px] text-gray-700">{s.name}</span>
                  <div className="flex gap-px">{[1, 2, 3, 4].map(n => (
                    <div key={n} className="w-1.5 h-1.5 rounded-full"
                      style={{ background: n <= (s.level === 'Expert' ? 4 : s.level === 'Advanced' ? 3 : s.level === 'Intermediate' ? 2 : 1) ? ac : '#e5e7eb' }} />
                  ))}</div>
                </div>
              ))}
            </div>
          )}
          {/* Education */}
          {resume.education?.length > 0 && (
            <div>
              <p className="text-[7px] font-black uppercase tracking-widest mb-1.5" style={{ color: ac }}>Education</p>
              {resume.education.map((e, i) => (
                <div key={i} className="mb-1.5">
                  <p className="text-[9px] font-bold text-gray-900">{e.degree}</p>
                  <p className="text-[8px] text-gray-500">{e.school} {e.year && `(${e.year})`}</p>
                </div>
              ))}
            </div>
          )}
          {/* Certifications */}
          {resume.certifications?.length > 0 && (
            <div>
              <p className="text-[7px] font-black uppercase tracking-widest mb-1.5" style={{ color: ac }}>Awards & Certs</p>
              {resume.certifications.map((c2, i) => (
                <p key={i} className="text-[8px] text-gray-700 mb-0.5">{c2.name} {c2.year && `(${c2.year})`}</p>
              ))}
            </div>
          )}
        </div>
        {/* Right Content */}
        <div className="flex-1 p-4 space-y-3">
          {resume.experience?.length > 0 && (
            <div>
              <p className="text-[7px] font-black uppercase tracking-widest mb-2" style={{ color: ac }}>Professional Experience</p>
              {resume.experience.map((e, i) => (
                <div key={i} className="mb-3 pb-3" style={{ borderBottom: i < resume.experience.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                  <div className="flex justify-between mb-0.5">
                    <span className="text-[10px] font-bold text-gray-900">{e.title}</span>
                    <span className="text-[8px] text-gray-500">{e.start_date}{e.end_date ? ` - ${e.end_date}` : ''}</span>
                  </div>
                  <p className="text-[9px] font-semibold mb-1" style={{ color: ac }}>{e.company} {e.location && `| ${e.location}`}</p>
                  {e.bullets?.filter(Boolean).map((b, bi) => (
                    <p key={bi} className="text-[9px] text-gray-600 ml-2 mb-0.5" dangerouslySetInnerHTML={{ __html: `• ${b}` }} />
                  ))}
                </div>
              ))}
            </div>
          )}
          {resume.projects?.length > 0 && (
            <div>
              <p className="text-[7px] font-black uppercase tracking-widest mb-1.5" style={{ color: ac }}>Projects</p>
              {resume.projects.map((p, i) => (
                <div key={i} className="mb-1.5">
                  <span className="text-[9px] font-bold text-gray-900">{p.name}</span>
                  {p.description && <p className="text-[8px] text-gray-600" dangerouslySetInnerHTML={{ __html: p.description }} />}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---- MODERN PREVIEW ----
function ModernPreview({ resume, photo, accentColor }) {
  const c = resume.contact || {};
  const ac = accentColor || '#0D9488';
  return (
    <div className="bg-white text-gray-900 rounded-xl overflow-hidden shadow-lg" style={{ fontSize: 11 }} data-testid="resume-preview">
      <div className="flex">
        <div className="w-[35%] p-5" style={{ background: '#1a202c', color: '#e2e8f0' }}>
          {photo && <img src={`data:image/jpeg;base64,${photo}`} alt="" className="w-20 h-20 rounded-xl mx-auto mb-3 object-cover" />}
          <h2 className="text-base font-bold text-white text-center mb-0.5">{c.name || 'Your Name'}</h2>
          {c.email && <p className="text-[9px] text-center text-gray-400">{c.email}</p>}
          {c.phone && <p className="text-[9px] text-center text-gray-400">{c.phone}</p>}
          {c.location && <p className="text-[9px] text-center text-gray-400 mb-3">{c.location}</p>}
          {resume.skills?.length > 0 && (
            <div className="mt-3">
              <p className="text-[8px] font-bold uppercase tracking-wider text-gray-500 mb-1.5">Skills</p>
              {resume.skills.map((s, i) => (
                <div key={i} className="flex justify-between"><span className="text-[9px]">{s.name}</span>
                  <span className="text-[7px] font-bold" style={{ color: ac }}>{s.level}</span></div>
              ))}
            </div>
          )}
          {resume.certifications?.length > 0 && (
            <div className="mt-3">
              <p className="text-[8px] font-bold uppercase tracking-wider text-gray-500 mb-1.5">Certifications</p>
              {resume.certifications.map((c2, i) => <p key={i} className="text-[9px] mb-0.5">{c2.name}</p>)}
            </div>
          )}
        </div>
        <div className="flex-1 p-5">
          {resume.summary && (
            <div className="mb-3">
              <p className="text-[8px] font-bold uppercase tracking-wider text-gray-400 mb-1">Summary</p>
              <p className="text-[10px] leading-relaxed text-gray-700" dangerouslySetInnerHTML={{ __html: resume.summary }} />
            </div>
          )}
          {resume.experience?.length > 0 && (
            <div className="mb-3">
              <p className="text-[8px] font-bold uppercase tracking-wider text-gray-400 mb-1.5">Experience</p>
              {resume.experience.map((e, i) => (
                <div key={i} className="mb-2.5">
                  <div className="flex justify-between">
                    <span className="text-[10px] font-bold text-gray-900">{e.title}</span>
                    <span className="text-[8px] text-gray-500">{e.start_date}{e.end_date ? ` - ${e.end_date}` : ''}</span>
                  </div>
                  <p className="text-[9px] font-semibold" style={{ color: ac }}>{e.company}</p>
                  {e.bullets?.filter(Boolean).map((b, bi) => <p key={bi} className="text-[9px] text-gray-600 ml-2" dangerouslySetInnerHTML={{ __html: `• ${b}` }} />)}
                </div>
              ))}
            </div>
          )}
          {resume.education?.length > 0 && (
            <div>
              <p className="text-[8px] font-bold uppercase tracking-wider text-gray-400 mb-1">Education</p>
              {resume.education.map((e, i) => (
                <div key={i} className="flex justify-between mb-1">
                  <span className="text-[10px] font-bold">{e.degree} <span className="font-normal text-gray-500">- {e.school}</span></span>
                  <span className="text-[8px] text-gray-500">{e.year}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---- CLASSIC PREVIEW ----
function ClassicPreview({ resume, photo, accentColor }) {
  const c = resume.contact || {};
  const ac = accentColor || '#0D9488';
  return (
    <div className="bg-white text-gray-900 rounded-xl overflow-hidden shadow-lg p-6" style={{ fontSize: 11 }} data-testid="resume-preview">
      <div className="text-center mb-4 pb-3" style={{ borderBottom: `2px solid ${ac}` }}>
        {photo && <img src={`data:image/jpeg;base64,${photo}`} alt="" className="w-16 h-16 rounded-full mx-auto mb-2 object-cover" />}
        <h2 className="text-lg font-bold text-gray-900">{c.name || 'Your Name'}</h2>
        <p className="text-[9px] text-gray-500">{[c.email, c.phone, c.location].filter(Boolean).join(' | ')}</p>
      </div>
      {resume.summary && (
        <div className="mb-3"><p className="text-[8px] font-bold uppercase text-gray-400 mb-1">Summary</p>
          <p className="text-[10px] leading-relaxed text-gray-700" dangerouslySetInnerHTML={{ __html: resume.summary }} /></div>
      )}
      {resume.experience?.length > 0 && (
        <div className="mb-3"><p className="text-[8px] font-bold uppercase text-gray-400 mb-1.5">Experience</p>
          {resume.experience.map((e, i) => (
            <div key={i} className="mb-2.5">
              <div className="flex justify-between"><span className="text-[10px] font-bold">{e.title} <span className="font-normal" style={{ color: ac }}>at {e.company}</span></span>
                <span className="text-[8px] text-gray-500">{e.start_date}{e.end_date ? ` - ${e.end_date}` : ''}</span></div>
              {e.bullets?.filter(Boolean).map((b, bi) => <p key={bi} className="text-[9px] text-gray-600 ml-3" dangerouslySetInnerHTML={{ __html: `• ${b}` }} />)}
            </div>
          ))}</div>
      )}
      {resume.education?.length > 0 && (
        <div className="mb-3"><p className="text-[8px] font-bold uppercase text-gray-400 mb-1">Education</p>
          {resume.education.map((e, i) => (
            <div key={i} className="flex justify-between mb-1"><span className="text-[10px] font-bold">{e.degree} <span className="font-normal text-gray-500">- {e.school}</span></span>
              <span className="text-[8px] text-gray-500">{e.year}</span></div>
          ))}</div>
      )}
      {resume.skills?.length > 0 && (
        <div><p className="text-[8px] font-bold uppercase text-gray-400 mb-1">Skills</p>
          <div className="flex flex-wrap gap-1">{resume.skills.map((s, i) => (
            <span key={i} className="text-[8px] font-semibold px-1.5 py-0.5 rounded bg-gray-100 text-gray-700">{s.name}</span>
          ))}</div></div>
      )}
    </div>
  );
}


// ---- WIZARD PANEL ----
function WizardPanel({ onApply }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('intro');
  const [sessionData, setSessionData] = useState({});
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await resumeAPI.wizard('intro', '', {});
        setMessages([{ role: 'wizard', content: res.data.response }]);
      } catch {} finally { setLoading(false); }
    })();
  }, []);

  const send = async () => {
    if (!input.trim() || loading) return;
    const msg = input.trim(); setInput('');
    setMessages(p => [...p, { role: 'user', content: msg }]);
    setLoading(true);
    try {
      const res = await resumeAPI.wizard(step, msg, sessionData);
      setMessages(p => [...p, { role: 'wizard', content: res.data.response }]);
      if (res.data.parsed_data && Object.keys(res.data.parsed_data).length) {
        setSessionData(p => ({ ...p, ...res.data.parsed_data }));
        onApply(res.data.parsed_data);
      }
      if (res.data.next_step) setStep(res.data.next_step);
    } catch {} finally { setLoading(false); }
  };

  return (
    <div className="space-y-4" data-testid="wizard-panel">
      <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className="max-w-[85%]">
              {m.role === 'wizard' && <div className="flex items-center gap-1 mb-1"><Bot className="w-3.5 h-3.5" style={{ color: '#8B5CF6' }} />
                <span className="text-[9px] font-bold" style={{ color: '#8B5CF6' }}>Resume Wizard</span></div>}
              <div className="px-3 py-2.5 rounded-2xl text-sm whitespace-pre-wrap"
                style={{ background: m.role === 'user' ? '#0D9488' : 'var(--t-bg-section)', color: m.role === 'user' ? '#fff' : 'var(--t-text)',
                  border: m.role === 'user' ? 'none' : '1px solid var(--t-border)' }}>{m.content}</div>
            </div>
          </div>
        ))}
        {loading && <div className="flex justify-start"><div className="px-3 py-2 rounded-2xl" style={{ background: 'var(--t-bg-section)', border: '1px solid var(--t-border)' }}>
          <Loader2 className="w-4 h-4 animate-spin" style={{ color: '#8B5CF6' }} /></div></div>}
        <div ref={endRef} />
      </div>
      <div className="flex gap-2">
        <Input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && send()}
          placeholder="Tell the wizard about yourself..." className="h-10 text-sm"
          style={{ background: 'var(--t-bg-input)', borderColor: 'var(--t-border)', color: 'var(--t-text)' }} data-testid="wizard-input" />
        <Button onClick={send} disabled={loading} className="h-10 px-4 rounded-xl" style={{ background: '#0D9488', color: '#fff' }} data-testid="wizard-send">
          <Send className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}


// ---- VERSIONS PANEL ----
function VersionsPanel({ onLoad }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saveName, setSaveName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []);
  const load = async () => { try { const r = await resumeAPI.versions(); setVersions(r.data || []); } catch {} finally { setLoading(false); } };
  const save = async () => {
    if (!saveName.trim()) return; setSaving(true);
    try { await resumeAPI.saveVersion(saveName.trim()); toast.success('Version saved'); setSaveName(''); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };
  const del = async (id) => { try { await resumeAPI.deleteVersion(id); toast.success('Deleted'); load(); } catch {} };

  return (
    <div className="space-y-4" data-testid="versions-panel">
      <div className="flex gap-2">
        <Input value={saveName} onChange={e => setSaveName(e.target.value)} placeholder="Version name (e.g. Tech Resume v2)"
          className="h-10 text-sm" style={{ background: 'var(--t-bg-input)', borderColor: 'var(--t-border)', color: 'var(--t-text)' }} data-testid="version-name-input" />
        <Button onClick={save} disabled={saving || !saveName.trim()} className="h-10 px-5 text-xs font-bold rounded-xl"
          style={{ background: '#0D9488', color: '#fff' }} data-testid="save-version-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Save</>}</Button>
      </div>
      {loading ? <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" style={{ color: '#0D9488' }} /></div> :
        versions.length === 0 ? <div className="text-center py-8"><History className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--t-text-muted)' }} />
          <p className="text-xs" style={{ color: 'var(--t-text-muted)' }}>No versions saved yet</p></div> :
          <div className="space-y-2">{versions.map(v => (
            <div key={v.version_id} className="nx-card p-3 flex items-center justify-between" data-testid={`version-${v.version_id}`}>
              <div><p className="text-sm font-bold" style={{ color: 'var(--t-text-heading)' }}>{v.name}</p>
                <p className="text-[9px]" style={{ color: 'var(--t-text-muted)' }}>{new Date(v.created_at).toLocaleDateString()}</p></div>
              <div className="flex gap-1">
                <button onClick={async () => { const r = await resumeAPI.loadVersion(v.version_id); onLoad(r.data.data, r.data.photo); toast.success(`Loaded: ${v.name}`); }}
                  className="text-[9px] font-bold px-2.5 py-1 rounded-lg" style={{ background: 'rgba(13,148,136,0.06)', color: '#0D9488' }}>Load</button>
                <button onClick={() => del(v.version_id)} className="text-[9px] font-bold px-2 py-1 rounded-lg" style={{ background: 'rgba(220,38,38,0.06)', color: '#DC2626' }}>
                  <Trash2 className="w-3 h-3" /></button>
              </div>
            </div>
          ))}</div>}
    </div>
  );
}


// ==== MAIN PAGE ====
export default function ResumePage() {
  const [resume, setResume] = useState(emptyResume);
  const [sections, setSections] = useState(DEFAULT_SECTIONS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeTab, setActiveTab] = useState('editor');
  const [template, setTemplate] = useState('impact');
  const [accentColor, setAccentColor] = useState('#0D9488');
  const [photo, setPhoto] = useState(null);
  const [openSections, setOpenSections] = useState({ contact: true, summary: true, experience: true });
  const [aiLoading, setAiLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [atsScore, setAtsScore] = useState(null);
  const [atsLoading, setAtsLoading] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [grammarChecking, setGrammarChecking] = useState(false);
  const fileRef = useRef(null);

  const completeness = calcCompleteness(resume);

  useEffect(() => {
    (async () => {
      try {
        const res = await resumeAPI.get();
        if (res.data.exists && res.data.data) setResume(res.data.data);
        const photoRes = await resumeAPI.getPhoto();
        if (photoRes.data?.photo) setPhoto(photoRes.data.photo);
      } catch {} finally { setLoading(false); }
    })();
    loadAtsScore();
  }, []);

  const loadAtsScore = async () => {
    setAtsLoading(true);
    try { const r = await resumeAPI.atsScore(); setAtsScore(r.data); } catch {} finally { setAtsLoading(false); }
  };

  const update = useCallback((path, value) => {
    setResume(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let obj = next;
      for (let i = 0; i < keys.length - 1; i++) {
        if (obj[keys[i]] === undefined) obj[keys[i]] = isNaN(keys[i + 1]) ? {} : [];
        obj = obj[keys[i]];
      }
      obj[keys[keys.length - 1]] = value;
      return next;
    });
    setHasChanges(true);
  }, []);

  const addItem = useCallback((section, tpl) => {
    setResume(prev => ({ ...prev, [section]: [...(prev[section] || []), { ...tpl }] }));
    setHasChanges(true);
    setOpenSections(prev => ({ ...prev, [section]: true }));
  }, []);

  const removeItem = useCallback((section, index) => {
    setResume(prev => ({ ...prev, [section]: prev[section].filter((_, i) => i !== index) }));
    setHasChanges(true);
  }, []);

  const saveResume = async () => {
    setSaving(true);
    try {
      await resumeAPI.save(resume);
      await resumeAPI.savePreferences(template, accentColor);
      toast.success('Resume saved'); setHasChanges(false);
      loadAtsScore();
    } catch { toast.error('Failed to save'); } finally { setSaving(false); }
  };

  const syncProfile = async () => {
    setSyncing(true);
    try { const r = await resumeAPI.syncProfile(); setResume(r.data.data); setHasChanges(true); toast.success('Resume populated from profile'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to sync'); } finally { setSyncing(false); }
  };

  const uploadResume = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try { const r = await resumeAPI.upload(file); setResume(r.data.parsed); setHasChanges(true); toast.success('Resume parsed!'); }
    catch (e2) { toast.error(e2.response?.data?.detail || 'Parse failed'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try { const r = await resumeAPI.uploadPhoto(file); setPhoto(r.data.photo); toast.success('Photo uploaded'); } catch { toast.error('Upload failed'); }
  };

  const magicRegenerate = async () => {
    setRegenerating(true);
    try {
      const r = await resumeAPI.magicRegenerate();
      setResume(r.data.data);
      setHasChanges(false);
      loadAtsScore();
      toast.success('Resume regenerated & saved as new version');
    } catch (e) { toast.error(e.response?.data?.detail || 'Regeneration failed'); }
    finally { setRegenerating(false); }
  };

  const createShareLink = async () => {
    try {
      const r = await resumeAPI.createShareLink();
      const baseUrl = process.env.REACT_APP_BACKEND_URL;
      setShareLink(`${baseUrl}/public/resume/${r.data.share_id}`);
      setShowShare(true);
    } catch { toast.error('Failed to create share link'); }
  };

  const grammarCheck = async () => {
    const allText = [
      resume.summary || '',
      ...(resume.experience || []).flatMap(e => [e.title || '', ...(e.bullets || [])]),
    ].filter(Boolean).join('\n');
    if (!allText || allText.length < 10) { toast.info('Not enough text to check'); return; }
    setGrammarChecking(true);
    try {
      const r = await resumeAPI.grammarCheck(allText);
      if (r.data.errors_found === 0) {
        toast.success('No grammar or spelling errors found!');
      } else {
        toast.info(`Found ${r.data.errors_found} issue(s): ${r.data.corrections.join(', ')}`);
        if (r.data.corrected_text && r.data.corrected_text !== allText) {
          const parts = r.data.corrected_text.split('\n');
          if (parts[0] && resume.summary) update('summary', parts[0]);
        }
      }
    } catch { toast.error('Grammar check failed'); }
    finally { setGrammarChecking(false); }
  };

  const aiSuggest = async (field, context) => {
    setAiLoading(true);
    try {
      const r = await resumeAPI.aiSuggest(field, context, '');
      const s = r.data.suggestion;
      if (field === 'summary') update('summary', s);
      else if (field === 'bullet') {
        const li = resume.experience.length - 1;
        if (li >= 0) update(`experience.${li}.bullets`, [...(resume.experience[li].bullets || []), s]);
      } else if (field === 'skills') {
        const ns = s.split(',').map(x => ({ name: x.trim(), level: 'Intermediate' }));
        update('skills', [...resume.skills, ...ns]);
      }
      toast.success('AI suggestion applied');
    } catch { toast.error('AI suggestion failed'); } finally { setAiLoading(false); }
  };

  const addCustomSection = () => {
    const name = prompt('Section name:');
    if (!name) return;
    const id = name.toLowerCase().replace(/\s+/g, '_');
    setSections(prev => [...prev, { id, label: name, icon: 'Star', type: 'list' }]);
    setResume(prev => ({ ...prev, [id]: [] }));
    setOpenSections(prev => ({ ...prev, [id]: true }));
  };

  const renameSectionLabel = (sectionId, newLabel) => {
    setSections(prev => prev.map(s => s.id === sectionId ? { ...s, label: newLabel } : s));
  };

  const applyWizardData = (data) => {
    if (data.summary) update('summary', data.summary);
    if (data.title && data.company) addItem('experience', { title: data.title, company: data.company, location: '', start_date: '', end_date: '', bullets: data.bullets || [] });
    if (data.degree) addItem('education', { degree: data.degree, school: data.school || '', year: data.year || '' });
    if (data.skills?.length) {
      const existing = resume.skills.map(s => s.name.toLowerCase());
      const ns = data.skills.filter(s => !existing.includes((s.name || s).toString().toLowerCase()));
      if (ns.length) update('skills', [...resume.skills, ...ns.map(s => typeof s === 'string' ? { name: s, level: 'Intermediate' } : s)]);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="w-6 h-6 animate-spin mr-2" style={{ color: '#0D9488' }} />
      <span className="text-sm" style={{ color: 'var(--t-text-muted)' }}>Loading Resume Studio...</span>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto" data-testid="resume-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-5">
        <div>
          <p className="text-[10px] font-bold tracking-[0.2em] uppercase" style={{ color: accentColor }}>Career Intelligence Engine</p>
          <h1 className="text-2xl font-extrabold" style={{ color: 'var(--t-text-heading)' }}>Resume Studio</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ScoreGauge score={atsScore?.score || 0} label="ATS Score" />
          <ScoreGauge score={completeness} label="Complete" />
          {atsScore?.grade && (
            <span className="text-xs font-black px-2 py-1 rounded-lg"
              style={{ background: atsScore.score >= 80 ? '#16A34A15' : '#F5841F15', color: atsScore.score >= 80 ? '#16A34A' : '#F5841F' }}>
              {atsScore.grade}
            </span>
          )}
          {/* Color picker */}
          <div className="relative">
            <button onClick={() => setShowColorPicker(!showColorPicker)}
              className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ background: accentColor }}
              data-testid="color-picker-btn">
              <Palette className="w-3.5 h-3.5 text-white" />
            </button>
            {showColorPicker && (
              <div className="absolute top-10 right-0 z-50 p-2 rounded-xl flex flex-wrap gap-1.5 w-40"
                style={{ background: 'var(--t-bg-card)', border: '1px solid var(--t-border)', boxShadow: '0 8px 24px rgba(0,0,0,0.3)' }}>
                {ACCENT_COLORS.map(c => (
                  <button key={c} onClick={() => { setAccentColor(c); setShowColorPicker(false); setHasChanges(true); }}
                    className="w-7 h-7 rounded-lg transition-all" style={{ background: c, outline: accentColor === c ? '2px solid white' : 'none' }}
                    data-testid={`color-${c}`} />
                ))}
              </div>
            )}
          </div>
          <Button onClick={syncProfile} disabled={syncing} className="h-8 px-3 text-[9px] font-bold rounded-lg"
            style={{ background: '#3B82F615', color: '#3B82F6', border: '1px solid #3B82F620' }} data-testid="sync-profile-btn">
            {syncing ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />} Sync
          </Button>
          <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="h-8 px-3 text-[9px] font-bold rounded-lg"
            style={{ background: '#8B5CF615', color: '#8B5CF6', border: '1px solid #8B5CF620' }} data-testid="upload-resume-btn">
            {uploading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Upload className="w-3 h-3 mr-1" />} Upload
          </Button>
          <input ref={fileRef} type="file" accept=".pdf,.docx" className="hidden" onChange={uploadResume} />
          <Button onClick={magicRegenerate} disabled={regenerating} className="h-8 px-3 text-[9px] font-bold rounded-lg"
            style={{ background: '#EC489915', color: '#EC4899', border: '1px solid #EC489920' }} data-testid="magic-regen-btn">
            {regenerating ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Wand2 className="w-3 h-3 mr-1" />} Magic
          </Button>
          <Button onClick={grammarCheck} disabled={grammarChecking} className="h-8 px-3 text-[9px] font-bold rounded-lg"
            style={{ background: '#6366F115', color: '#6366F1', border: '1px solid #6366F120' }} data-testid="grammar-check-btn">
            {grammarChecking ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <CheckCircle className="w-3 h-3 mr-1" />} Grammar
          </Button>
          <Button onClick={createShareLink} className="h-8 px-3 text-[9px] font-bold rounded-lg"
            style={{ background: '#16A34A15', color: '#16A34A', border: '1px solid #16A34A20' }} data-testid="share-resume-btn">
            <Share2 className="w-3 h-3 mr-1" /> Share
          </Button>
          <Button onClick={saveResume} disabled={saving || !hasChanges} className="h-8 px-4 text-[9px] font-bold rounded-lg"
            style={{ background: hasChanges ? accentColor : 'var(--t-bg-section)', color: hasChanges ? '#fff' : 'var(--t-text-muted)' }}
            data-testid="save-resume-btn">
            {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />} Save
          </Button>
        </div>
      </div>

      {/* Share Modal */}
      <AnimatePresence>
        {showShare && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
            className="nx-card p-4 mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Share2 className="w-4 h-4" style={{ color: '#16A34A' }} />
              <span className="text-xs font-bold" style={{ color: 'var(--t-text-heading)' }}>Public Resume Link</span>
            </div>
            <div className="flex items-center gap-2">
              <code className="text-[10px] px-3 py-1 rounded-lg" style={{ background: 'var(--t-bg-section)', color: '#0D9488' }}>
                {shareLink}
              </code>
              <button onClick={() => { navigator.clipboard.writeText(shareLink); toast.success('Link copied'); }}
                className="p-1.5 rounded-lg" style={{ background: 'rgba(13,148,136,0.06)' }} data-testid="copy-share-link">
                <Copy className="w-3.5 h-3.5" style={{ color: '#0D9488' }} />
              </button>
              <button onClick={() => setShowShare(false)} className="p-1">
                <X className="w-3.5 h-3.5" style={{ color: 'var(--t-text-muted)' }} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Completeness Bar */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[9px] font-bold uppercase tracking-wider" style={{ color: 'var(--t-text-muted)' }}>Resume Completeness</span>
          <span className="text-[9px] font-black" style={{ color: completeness === 100 ? '#16A34A' : '#F5841F' }}>{completeness}%</span>
        </div>
        <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--t-border)' }}>
          <motion.div className="h-full rounded-full" style={{ background: completeness === 100 ? '#16A34A' : accentColor }}
            initial={{ width: 0 }} animate={{ width: `${completeness}%` }} transition={{ duration: 0.8 }} />
        </div>
      </div>

      {/* ATS Tips */}
      {atsScore?.tips?.length > 0 && (
        <div className="flex items-start gap-2 mb-4 p-3 rounded-xl"
          style={{ background: 'rgba(245,132,31,0.05)', border: '1px solid rgba(245,132,31,0.12)' }}>
          <Shield className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#F5841F' }} />
          <div>
            <p className="text-[9px] font-bold uppercase mb-1" style={{ color: '#F5841F' }}>ATS Optimization Tips</p>
            {atsScore.tips.map((t, i) => <p key={i} className="text-[10px]" style={{ color: 'var(--t-text-muted)' }}>• {t}</p>)}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b mb-5" style={{ borderColor: 'var(--t-border)' }}>
        {TABS.map(tab => {
          const Icon = tab.icon;
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className="flex items-center gap-1.5 px-4 py-3 -mb-px transition-all"
              style={{ borderBottom: activeTab === tab.id ? `2px solid ${accentColor}` : '2px solid transparent',
                color: activeTab === tab.id ? accentColor : 'var(--t-text-muted)' }}
              data-testid={`tab-${tab.id}`}>
              <Icon className="w-3.5 h-3.5" /><span className="text-xs font-bold">{tab.label}</span>
            </button>
          );
        })}
      </div>

      <AnimatePresence mode="wait">
        <motion.div key={activeTab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.15 }}>

          {/* EDITOR */}
          {activeTab === 'editor' && (
            <div className="space-y-3">
              {/* Template + Add Section */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex gap-1.5">
                  {TEMPLATES.map(t => (
                    <button key={t.id} onClick={() => { setTemplate(t.id); setHasChanges(true); }}
                      className="px-3 py-1.5 rounded-lg text-[10px] font-bold"
                      style={{ background: template === t.id ? `${accentColor}12` : 'transparent',
                        color: template === t.id ? accentColor : 'var(--t-text-muted)',
                        border: `1px solid ${template === t.id ? `${accentColor}30` : 'var(--t-border)'}` }}
                      data-testid={`tpl-${t.id}`}>{t.name}</button>
                  ))}
                </div>
                <button onClick={addCustomSection}
                  className="flex items-center gap-1 text-[10px] font-bold px-3 py-1.5 rounded-lg"
                  style={{ color: accentColor, background: `${accentColor}08`, border: `1px solid ${accentColor}15` }}
                  data-testid="add-section-btn">
                  <Plus className="w-3 h-3" /> Add Section
                </button>
              </div>

              {/* Draggable Sections */}
              <Reorder.Group axis="y" values={sections} onReorder={setSections}>
                {sections.map(sec => {
                  const isOpen = openSections[sec.id];
                  const SIcon = ICON_MAP[sec.icon] || Star;
                  return (
                    <Reorder.Item key={sec.id} value={sec} className="mb-3">
                      <div className="nx-card overflow-hidden" data-testid={`section-${sec.id}`}>
                        <div className="flex items-center p-3.5 cursor-grab active:cursor-grabbing"
                          onClick={() => setOpenSections(p => ({ ...p, [sec.id]: !p[sec.id] }))}>
                          <GripVertical className="w-4 h-4 mr-2 flex-shrink-0" style={{ color: 'var(--t-text-muted)' }} />
                          <SIcon className="w-4 h-4 mr-2 flex-shrink-0" style={{ color: accentColor }} />
                          <input value={sec.label}
                            onChange={e => { e.stopPropagation(); renameSectionLabel(sec.id, e.target.value); }}
                            onClick={e => e.stopPropagation()}
                            className="text-sm font-bold bg-transparent border-none outline-none flex-1"
                            style={{ color: 'var(--t-text-heading)' }}
                            data-testid={`section-title-${sec.id}`} />
                          {sec.required && <span className="text-[7px] font-bold px-1 py-0.5 rounded mr-2"
                            style={{ background: `${accentColor}08`, color: accentColor }}>REQ</span>}
                          <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                            style={{ color: 'var(--t-text-muted)' }} />
                        </div>
                        <AnimatePresence>
                          {isOpen && (
                            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                              <div className="px-4 pb-4">
                                {sec.id === 'contact' && <ContactEditor contact={resume.contact || {}} update={update} photo={photo} onPhotoUpload={uploadPhoto} />}
                                {sec.id === 'summary' && (
                                  <div className="space-y-2">
                                    <RichTextArea value={resume.summary} onChange={v => update('summary', v)}
                                      placeholder="Write a compelling professional summary..." rows={4} />
                                    <button onClick={() => aiSuggest('summary', { job_title: resume.experience?.[0]?.title, skills: resume.skills?.map(s => s.name).join(', ') })}
                                      disabled={aiLoading} className="text-[9px] font-bold flex items-center gap-1 px-2.5 py-1 rounded-lg"
                                      style={{ color: '#8B5CF6', background: 'rgba(139,92,246,0.06)' }} data-testid="ai-summary-btn">
                                      {aiLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} AI Generate</button>
                                  </div>
                                )}
                                {sec.id === 'experience' && <ExperienceEditor items={resume.experience || []} update={update} onAdd={addItem} onRemove={removeItem} onAiSuggest={aiSuggest} />}
                                {sec.id === 'education' && <EducationEditor items={resume.education || []} update={update} onAdd={addItem} onRemove={removeItem} />}
                                {sec.id === 'skills' && <SkillsEditor items={resume.skills || []} update={update} onAiSuggest={aiSuggest} />}
                                {(sec.type === 'list' && sec.id !== 'skills') && (
                                  <ListEditor items={resume[sec.id] || []} section={sec.id} update={update}
                                    onAdd={addItem} onRemove={removeItem}
                                    fields={sec.id === 'certifications' ? [
                                      { key: 'name', ph: 'Certification name' }, { key: 'issuer', ph: 'Issuing org' }, { key: 'year', ph: 'Year' }
                                    ] : sec.id === 'projects' ? [
                                      { key: 'name', ph: 'Project name' }, { key: 'description', ph: 'Description', rich: true },
                                      { key: 'technologies', ph: 'Technologies' }, { key: 'url', ph: 'URL' }
                                    ] : [
                                      { key: 'name', ph: 'Item name' }, { key: 'description', ph: 'Description', rich: true }
                                    ]} />
                                )}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </Reorder.Item>
                  );
                })}
              </Reorder.Group>
            </div>
          )}

          {/* PREVIEW */}
          {activeTab === 'preview' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex gap-1.5">{TEMPLATES.map(t => (
                  <button key={t.id} onClick={() => setTemplate(t.id)} className="px-3 py-1.5 rounded-lg text-[10px] font-bold"
                    style={{ background: template === t.id ? `${accentColor}12` : 'var(--t-bg-section)',
                      color: template === t.id ? accentColor : 'var(--t-text-muted)',
                      border: `1px solid ${template === t.id ? `${accentColor}30` : 'var(--t-border)'}` }}
                    data-testid={`preview-tpl-${t.id}`}>{t.name} ({t.cols}-col)</button>
                ))}</div>
                <a href={resumeAPI.exportPDF(template)} target="_blank" rel="noopener noreferrer"
                  className="h-8 px-4 text-[10px] font-bold rounded-xl flex items-center gap-1.5"
                  style={{ background: accentColor, color: '#fff' }}><Download className="w-3.5 h-3.5" /> PDF</a>
              </div>
              <div className="flex justify-center">
                <div style={{ width: 595, transform: 'scale(0.9)', transformOrigin: 'top center' }}>
                  {template === 'impact' && <ImpactPreview resume={resume} photo={photo} accentColor={accentColor} />}
                  {template === 'modern' && <ModernPreview resume={resume} photo={photo} accentColor={accentColor} />}
                  {(template === 'classic' || template === 'executive') && <ClassicPreview resume={resume} photo={photo} accentColor={accentColor} />}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'wizard' && <WizardPanel onApply={applyWizardData} />}
          {activeTab === 'versions' && <VersionsPanel onLoad={(d, p) => { setResume(d); if (p) setPhoto(p); setHasChanges(true); }} />}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
